import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { PortfolioCreditAgreementContainer } from 'packages/portfolio/ui/creditAgreement/PortfolioCreditAgreement';
import {
  PortfolioTabOptionKeys,
  PortfolioTemplate,
} from 'packages/portfolio/ui/shared/PortfolioTemplate';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const PortfolioCreditAgreementView = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const creditFacilityId = query.get(creditFacilityIdQueryParam);

  let routeLevelError = false;

  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (!userData || !userData.user) {
    return <Skeleton data-testid="loading-credit-agreement" height="600px" />;
  }

  // NOTE - this component assumes it is being invoked by lender/borrower dashboard
  // by getting orgId by from user ctx, along with hardcode routing.
  // this component should likely be moved to borrower/lender section since this
  // cannot be called by admin, or these properties should be passed in from higher scope
  const {
    user: {
      organizationId,
      organization: { creditFacilities, slug: orgSlug },
    },
  } = userData;

  const creditFacilitySlug = _.find(
    creditFacilities,
    ({ id }) => id === creditFacilityId,
  )?.slug;

  if (!creditFacilityId || !creditFacilitySlug) {
    navigate('/portfolio/overview/facilities');
    toast({
      ...errorToastConfig,
      id: 'errorLoadPortfolioCreditAgreementNoCreditFacility',
      title: 'Please select a credit facility.',
    });
    return null;
  }

  return (
    <Box width="100%" padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            routeLevelError = true;
            toast({
              ...errorToastConfig,
              id: 'errorLoadCreditAgreement',
              title: 'Error loading portfolio credit agreement route',
            });
            navigate('/');
          }
        }}
      >
        <PortfolioTemplate
          organizationId={organizationId}
          creditFacilitySlug={creditFacilitySlug}
          selectedTab={PortfolioTabOptionKeys.creditAgreement}
        >
          <PortfolioCreditAgreementContainer
            creditFacilityId={creditFacilityId}
            orgSlug={orgSlug}
            organizationId={organizationId}
          />
        </PortfolioTemplate>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
